/* ------------------------------------------------------------------------- *
 * SHOWQ — delivery promise controller (product page).
 *
 * CONTRACT — other agents build against this and it does not change afterwards:
 *   every [data-showq-eta] node ON THE PAGE is repainted with the current earliest
 *   delivery sentence, on a 60s tick, on visibilitychange, on pageshow and on the
 *   theme's 'variant:change'. Nodes are RE-QUERIED on every paint, because the
 *   sticky add-to-cart bar lives inside <product-rerender>, which replaces its own
 *   DOM on every variant selection — the exact mechanism that kills the Taly
 *   widget's click handler today.
 *
 *     data-showq-eta="short"  -> the bare date line, for the one-line sticky mirror.
 *     data-showq-eta          -> the full sentence.
 *
 * It reads the config island snippets/showq-delivery-promise.liquid emitted, and it
 * computes NOTHING itself: the calendar comes from window.ShowqCalendar and the clock
 * from window.ShowqTime, both in assets/showq-time.js, shared with the cart picker so
 * the two surfaces cannot disagree about what day it is.
 *
 * FAIL OPEN. No config, no clock, an unparseable island or an empty calendar leaves the
 * server's first paint alone or empties the node — never a placeholder, never a duration
 * standing in for a date, never an invented day.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.__showqPromise) return;
  window.__showqPromise = true;

  var CFG = null;
  var TICK = 60000;
  var lastSig = '';

  function pad2(n) { n = Number(n) || 0; return (n < 10 ? '0' : '') + n; }

  function readConfig() {
    var el = document.querySelector('[data-showq-promise-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* Sentinel -> node. Every date and every duration crosses into copy as a placeholder inside a
     <bdi>, never by string concatenation, so a translator can reorder the sentence and the Latin
     digits stay isolated inside Arabic. */
  function compose(node, pattern, dateStr, cdStr) {
    clear(node);
    var re = /@@[DC]@@/g, at = 0, m, b;
    while ((m = re.exec(pattern)) !== null) {
      if (m.index > at) node.appendChild(document.createTextNode(pattern.slice(at, m.index)));
      b = document.createElement('bdi');
      b.className = 'oysa-num';
      b.textContent = (m[0] === '@@D@@') ? dateStr : cdStr;
      node.appendChild(b);
      at = m.index + m[0].length;
    }
    if (at < pattern.length) node.appendChild(document.createTextNode(pattern.slice(at)));
  }

  function paint() {
    var T = window.ShowqTime, C = window.ShowqCalendar;
    var nodes = document.querySelectorAll('[data-showq-eta]');
    if (!nodes.length) return;
    if (!CFG || !T || !T.supported || !C) return;   /* leave the server's first paint alone */

    var cal = C.build(CFG, {
      applicable: CFG.applicable !== false,
      minLead: Number(CFG.minLeadDays) || 0,
      /* A product page has no area picker, so it has no destination and may not offer today.
         allowToday is the one switch the all-areas same-day guard flips. */
      sameDayAllowed: CFG.allowToday === true
    });
    var first = C.firstOpen(cal);

    var txt = CFG.txt || {};
    var full = '', brief = '', dateStr = '', cdStr = '';

    if (first) {
      dateStr = T.formatDate(first.iso, { weekday: 'short', day: 'numeric', month: 'short' });
      brief = txt.dateOnly || '';
      full = brief;

      /* The countdown is only honest when crossing the cut-off actually MOVES the date. With
         same-day off it does not — the earliest day is tomorrow either side of 17:00 — so the
         shipped floor carries no timer at all. It lights up with allowToday, and never before. */
      if (CFG.allowToday === true) {
        var nowMs = T.now();
        var today = T.civil(nowMs);
        if (first.iso === today.iso) {
          var cutoffMs = T.toEpoch(today.iso, pad2(CFG.cutoffHour) + ':' + pad2(CFG.cutoffMinute));
          var left = cutoffMs - nowMs;
          if (left > 0 && txt.today && txt.hm) {
            var mins = Math.floor(left / 60000);
            cdStr = String(txt.hm)
              .replace('@@H@@', String(Math.floor(mins / 60)))
              .replace('@@M@@', String(mins % 60));
            full = txt.today;
          }
        }
      }
    }

    var sig = full + '' + brief + '' + dateStr + '' + cdStr;
    if (sig === lastSig) return;   /* an aria-live region must not re-announce an unchanged line */
    lastSig = sig;

    var i, n, pattern;
    for (i = 0; i < nodes.length; i++) {
      n = nodes[i];
      pattern = (n.getAttribute('data-showq-eta') === 'short') ? brief : full;
      if (!first || !pattern) { clear(n); continue; }
      compose(n, pattern, dateStr, cdStr);
    }

    /* The supporting half belongs to the same statement: with no date there is nothing to choose
       a time for, so it goes with it rather than standing on its own. */
    var picks = document.querySelectorAll('.showq-eta__pick');
    for (i = 0; i < picks.length; i++) picks[i].hidden = !first;
  }

  var started = false;
  function start() {
    if (started) return;
    started = true;
    CFG = readConfig();
    if (!CFG) return;
    /* Never trust a server stamp off a page that may have been cached: a stale epoch would freeze
       the clock in the past, which is exactly how a delivery date goes quietly wrong. Two minutes
       is generous for a fresh render and far short of any useful cache age. */
    if (window.ShowqTimeInit) window.ShowqTimeInit(CFG.serverEpoch, 120000);
    paint();
    setInterval(paint, TICK);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) paint(); });
    window.addEventListener('pageshow', paint);
    /* Impact dispatches this on the product form with bubbles:true. The sticky bar's DOM has just
       been replaced by then, so the re-query inside paint() is what keeps the mirror alive. */
    document.addEventListener('variant:change', paint);
  }

  if (window.ShowqCalendar) start();
  else document.addEventListener('showq:time:ready', start);
})();
